import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Building2, Users, MapPin, CreditCard, Loader2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@atrvd/saas-ui'
import { OrganizationStatusBadge } from '@/components/admin/OrganizationStatusBadge'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import {
  useOrganizationFullDetails,
  useOrganizationMembers,
  useOrganizationFacilities,
  type OrganizationFullDetails,
  type OrganizationMember,
  type OrganizationFacility,
} from '@/hooks/useOrganizationAdmin'
import {
  useSuspendOrganization,
  useUnsuspendOrganization,
  useDeleteOrganization,
} from '@/hooks/useOrganizationActions'
import { PLAID_KEYS } from '@/hooks/usePlaid'
import { createTenantDb } from '@/lib/db-tenant'
import type { TenantPlaidItem } from '@/lib/db-tenant'
import { formatDate, formatDateTime } from '@dcp/shared/utils/formatters'

/**
 * Stats card component for displaying organization metrics
 */
function StatCard({ label, value, icon: Icon }: { label: string; value: number; icon: typeof Building2 }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className="rounded-lg bg-muted p-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Overview tab content showing organization details and owner information
 */
function OverviewTab({ details }: { details: OrganizationFullDetails }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Organization Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Organization ID</p>
              <p className="font-mono text-sm">{details.organization.id}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <OrganizationStatusBadge status={details.organization.status} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p>{formatDate(details.organization.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last Updated</p>
              <p>{formatDate(details.organization.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {details.owner && (
        <Card>
          <CardHeader>
            <CardTitle>Organization Owner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p>{details.owner.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p>{details.owner.fullName || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/**
 * Members tab content showing organization team members
 */
function MembersTab({ members, isLoading }: { members: OrganizationMember[]; isLoading: boolean }) {
  if (isLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team Members</CardTitle>
        <CardDescription>{members.length} members in this organization</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {members.map((member) => (
            <div key={member.id} className="flex items-center justify-between border-b pb-4 last:border-0">
              <div>
                <p className="font-medium">{member.fullName || member.email}</p>
                <p className="text-sm text-muted-foreground">{member.email}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium capitalize">{member.role.replace('_', ' ')}</p>
                <p className="text-xs text-muted-foreground">
                  Joined {formatDate(member.createdAt)}
                </p>
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <p className="text-center text-muted-foreground py-4">No members found</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Facilities tab content showing organization facilities
 */
function FacilitiesTab({ facilities, isLoading }: { facilities: OrganizationFacility[]; isLoading: boolean }) {
  if (isLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Facilities</CardTitle>
        <CardDescription>{facilities.length} facilities in this organization</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {facilities.map((facility) => (
            <div key={facility.id} className="flex items-center justify-between border-b pb-4 last:border-0">
              <div>
                <p className="font-medium">{facility.name}</p>
                {facility.code && <p className="text-sm text-muted-foreground">Code: {facility.code}</p>}
              </div>
              <div className="text-right">
                <p className="text-sm">
                  {facility.bankConnectionsCount} bank connection{facility.bankConnectionsCount !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {facility.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
          ))}
          {facilities.length === 0 && (
            <p className="text-center text-muted-foreground py-4">No facilities found</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Banking tab content showing connected banking institutions
 */
function BankingTab({ orgId }: { orgId: string }) {
  // Admin-only view: uses orgId from URL params (not current user's org).
  // Uses shared PLAID_KEYS to avoid cache key divergence with usePlaidItems hook.
  const { data: plaidItems = [], isLoading } = useQuery<TenantPlaidItem[]>({
    queryKey: PLAID_KEYS.items(orgId),
    queryFn: async () => {
      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.plaidItems.list()
      if (error) throw error
      return data
    },
    enabled: !!orgId,
  })

  if (isLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Banking Connections</CardTitle>
        <CardDescription>{plaidItems.length} connected banks</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {plaidItems.map((item) => (
            <div key={item.id} className="flex items-center justify-between border-b pb-4 last:border-0">
              <div>
                <p className="font-medium">{item.institution_name || 'Unknown Bank'}</p>
                <p className="text-sm text-muted-foreground capitalize">{item.status}</p>
              </div>
              <div className="text-right">
                {item.plaid_sync_cursors?.last_sync_at && (
                  <p className="text-xs text-muted-foreground">
                    Last sync: {formatDateTime(item.plaid_sync_cursors.last_sync_at)}
                  </p>
                )}
              </div>
            </div>
          ))}
          {plaidItems.length === 0 && (
            <p className="text-center text-muted-foreground py-4">No banking connections</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Organization Detail Page for admin tenant management
 * Displays comprehensive organization information including stats, members, facilities, and banking
 */
export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')

  // Dialogs state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Queries
  const { data: details, isLoading, error } = useOrganizationFullDetails(id)
  const { data: members = [], isLoading: membersLoading } = useOrganizationMembers(id)
  const { data: facilities = [], isLoading: facilitiesLoading } = useOrganizationFacilities(id)

  // Mutations
  const suspendOrg = useSuspendOrganization()
  const unsuspendOrg = useUnsuspendOrganization()
  const deleteOrg = useDeleteOrganization()

  /**
   * Handle organization suspension
   */
  const handleSuspend = async () => {
    if (!id) return
    try {
      await suspendOrg.mutateAsync(id)
      toast.success('Organization suspended')
      setSuspendDialogOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to suspend organization')
    }
  }

  /**
   * Handle organization reactivation
   */
  const handleUnsuspend = async () => {
    if (!id) return
    try {
      await unsuspendOrg.mutateAsync(id)
      toast.success('Organization reactivated')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to reactivate organization')
    }
  }

  /**
   * Handle organization deletion
   */
  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteOrg.mutateAsync(id)
      toast.success('Organization deleted')
      navigate('/admin/organizations')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete organization')
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state
  if (error || !details) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load organization</p>
        <Button variant="outline" onClick={() => navigate('/admin/organizations')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
      </div>
    )
  }

  const isSuspended = details.organization.status === 'suspended'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-4">
            <Link to="/admin/organizations">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Organizations
            </Link>
          </Button>
          <div className="flex items-center gap-4">
            {details.organization.logoUrl ? (
              <img
                src={details.organization.logoUrl}
                alt={`${details.organization.name} logo`}
                className="h-14 w-14 rounded-lg object-cover"
              />
            ) : (
              <div className="rounded-lg bg-muted p-3">
                <Building2 className="h-8 w-8" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">{details.organization.name}</h1>
                <OrganizationStatusBadge status={details.organization.status} />
              </div>
              <p className="text-muted-foreground">
                Created {formatDate(details.organization.createdAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {isSuspended ? (
            <Button
              variant="outline"
              onClick={handleUnsuspend}
              disabled={unsuspendOrg.isPending}
            >
              {unsuspendOrg.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reactivate
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setSuspendDialogOpen(true)}
            >
              Suspend
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Users" value={details.stats.usersCount} icon={Users} />
        <StatCard label="Facilities" value={details.stats.facilitiesCount} icon={MapPin} />
        <StatCard label="Financial Institutions" value={details.stats.bankConnectionsCount} icon={CreditCard} />
        <StatCard label="Accounts" value={details.stats.accountsCount} icon={CreditCard} />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="facilities">Facilities</TabsTrigger>
          <TabsTrigger value="banking">Banking</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab details={details} />
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <MembersTab members={members} isLoading={membersLoading} />
        </TabsContent>

        <TabsContent value="facilities" className="mt-6">
          <FacilitiesTab facilities={facilities} isLoading={facilitiesLoading} />
        </TabsContent>

        <TabsContent value="banking" className="mt-6">
          {id && <BankingTab orgId={id} />}
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={suspendDialogOpen}
        onOpenChange={setSuspendDialogOpen}
        title="Suspend Organization"
        description={`Are you sure you want to suspend "${details.organization.name}"? Users will not be able to log in until the organization is reactivated.`}
        confirmText="Suspend"
        variant="destructive"
        onConfirm={handleSuspend}
        isLoading={suspendOrg.isPending}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Organization"
        description={`Are you sure you want to permanently delete "${details.organization.name}"? This action cannot be undone and will remove all associated data.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteOrg.isPending}
      />
    </div>
  )
}
